import { MenuItem, Stack, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router-dom';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CrudPage } from '../../shared/components/CrudPage';
import { resources } from '../../core/services/resources';
import type { LineOfBiz, LineOfBizStatus } from '../../core/types/data-models';

const LINE_OF_BIZ_STATUSES: LineOfBizStatus[] = ['ACTIVE', 'DISABLED', 'DRAFT'];
import { formatDate } from '../../shared/utils/format';
import { useTenant } from '../../core/contexts/TenantContext';
import { LineOfBizSelect } from './LineOfBizSelect';

export function LinesOfBusinessPage() {
  const { activeTenantId } = useTenant();

  // Same cache key as LineOfBizSelect so the two share data and the table
  // gets the LOB list "for free" once the dialog has been opened (and vice
  // versa). 200 is plenty for any realistic tenant; bump if needed.
  const lookup = useQuery({
    queryKey: ['lines-of-business', 'select'],
    queryFn: () => resources.linesOfBusiness.list({ pageSize: 200, sort: 'name' }),
  });

  const parentName = useMemo(() => {
    const map = new Map<number, string>();
    for (const lob of lookup.data?.data ?? []) {
      map.set(lob.id, lob.name);
    }
    return map;
  }, [lookup.data]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 220,
        renderCell: (p) => (
          <RouterLink to={`/lines-of-business/${p.row.id}`}>{p.value as string}</RouterLink>
        ),
      },
      { field: 'description', headerName: 'Description', flex: 2, minWidth: 280 },
      { field: 'status', headerName: 'Status', width: 120 },
      {
        field: 'parentId',
        headerName: 'Parent',
        width: 200,
        // Render the parent's name when we know it; fall back to "#<id>"
        // when the parent isn't in the cached list (e.g. truncated by the
        // 200-row cap or not yet loaded). Empty when there's no parent.
        renderCell: (p) => {
          const id = p.value as number | null | undefined;
          if (id == null) return '';
          return parentName.get(id) ?? `#${id}`;
        },
      },
      {
        field: 'reportTemplateCount',
        headerName: 'Report templates',
        width: 160,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v: unknown) =>
          typeof v === 'number' ? v.toLocaleString() : ((v ?? '') as string),
      },
      { field: 'createdAt', headerName: 'Created', width: 180, valueFormatter: (v) => formatDate(v as string) },
    ],
    [parentName],
  );

  return (
    <CrudPage<LineOfBiz>
      title="Lines of business"
      entityName="Line of business"
      description="Each LOB groups reports for a vertical, product line, or business unit."
      cacheKey={['lines-of-business']}
      service={resources.linesOfBusiness}
      columns={columns}
      defaultValues={{
        name: '',
        description: '',
        status: 'ACTIVE',
        parentId: null,
        customFieldATitle: null,
        customFieldBTitle: null,
        customFieldCTitle: null,
        tenantId: activeTenantId ?? undefined,
      }}
      beforeSubmit={(v) => ({ ...v, tenantId: v.tenantId ?? activeTenantId ?? undefined })}
      renderForm={({ values, setValues }) => (
        <Stack spacing={2}>
          <TextField
            label="Name"
            required
            value={values.name ?? ''}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
          />
          <TextField
            label="Description"
            multiline
            minRows={3}
            value={values.description ?? ''}
            onChange={(e) => setValues({ ...values, description: e.target.value })}
          />
          <TextField
            select
            label="Status"
            value={values.status ?? 'ACTIVE'}
            onChange={(e) =>
              setValues({ ...values, status: e.target.value as LineOfBizStatus })
            }
            helperText="ACTIVE lines run normally. DRAFT is hidden from production flows; DISABLED is paused."
          >
            {LINE_OF_BIZ_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <LineOfBizSelect
            value={values.parentId}
            onChange={(id) => setValues({ ...values, parentId: id })}
            // Don't let an LOB be its own parent.
            excludeId={typeof values.id === 'number' ? values.id : undefined}
          />
          <TextField
            label="Custom field A title"
            value={values.customFieldATitle ?? ''}
            onChange={(e) =>
              setValues({ ...values, customFieldATitle: e.target.value || null })
            }
          />
          <TextField
            label="Custom field B title"
            value={values.customFieldBTitle ?? ''}
            onChange={(e) =>
              setValues({ ...values, customFieldBTitle: e.target.value || null })
            }
          />
          <TextField
            label="Custom field C title"
            value={values.customFieldCTitle ?? ''}
            onChange={(e) =>
              setValues({ ...values, customFieldCTitle: e.target.value || null })
            }
          />
        </Stack>
      )}
    />
  );
}
